// src/components/map/FormLocationPickerField.jsx
import React, { useRef, useState, useEffect } from "react";
import { Field, ErrorMessage } from "formik";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { get, set } from "lodash";

const defaultPosition = [12.9716, 77.5946];
const defaultZoom = 5;
const myLocationZoom = 24;

// Fix marker icon issue in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

// Marker component with drag & click logic
const LocationMarker = ({ position, setPosition, setZoom }) => {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
      setZoom(myLocationZoom);
    },
  });

  return position ? (
    <Marker
      position={position}
      draggable
      eventHandlers={{
        dragend: (e) => {
          const { lat, lng } = e.target.getLatLng();
          setPosition([lat, lng]);
          setZoom(myLocationZoom);
        },
      }}
    />
  ) : null;
};

const LocationPickerMap = ({
  label,
  name,
  form,
  meta,
  className,
  labelClassName,
}) => {
  const mapRef = useRef(null);
  const formValue = get(form.values, name);

  const [position, setPosition] = useState(
    formValue?.lat && formValue?.lng ? [formValue.lat, formValue.lng] : null
  );

  const [zoom, setZoom] = useState(
    formValue?.lat && formValue?.lng ? myLocationZoom : defaultZoom
  );

  useEffect(() => {
    if (position) {
      const updatedValues = { ...form.values };
      set(updatedValues, name, { lat: position[0], lng: position[1] });
      form.setValues(updatedValues);
    }
  }, [position]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = [pos.coords.latitude, pos.coords.longitude];
        setPosition(coords);
        setZoom(14);

        const map = mapRef.current;
        if (map) {
          map.setView(coords, 14);
        }
      },
      (err) => {
        alert("Unable to retrieve your location");
        console.error(err);
      }
    );
  };

  const error = meta.touched && meta.error;

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          className={`block mb-2 text-fs_14 md:text-fs_16 font-fw_400 capitalize ${
            error ? "text-errorColor" : labelClassName
          }`}
        >
          {label}
        </label>
      )}

      <div className="mb-4">
        <button
          type="button"
          className="bg-blue-600 text-white px-4 py-2 rounded"
          onClick={getCurrentLocation}
        >
          Use My Location
        </button>
      </div>

      <MapContainer
        center={position || defaultPosition}
        zoom={zoom}
        scrollWheelZoom
        style={{ height: "300px", width: "100%" }}
        whenCreated={(mapInstance) => {
          mapRef.current = mapInstance;
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <LocationMarker
          position={position}
          setPosition={setPosition}
          setZoom={setZoom}
        />
      </MapContainer>

      {/* {position && (
        <div className="mt-4 text-sm text-gray-700">
          <p>
            <strong>Latitude:</strong> {position[0]}
          </p>
          <p>
            <strong>Longitude:</strong> {position[1]}
          </p>
        </div>
      )} */}

      <label className="text-errorColor text-fs_14 ps-1 h-[1rem]">
        <ErrorMessage name={name} />
      </label>
    </div>
  );
};

// Wrapper with Formik <Field>
export const FormLocationPickerField = ({
  label = "Select Location",
  name = "location",
  className = "",
  labelClassName = "",
}) => {
  return (
    <Field name={name}>
      {({ form, meta }) => (
        <LocationPickerMap
          form={form}
          meta={meta}
          name={name}
          label={label}
          className={className}
          labelClassName={labelClassName}
        />
      )}
    </Field>
  );
};
